import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { useToast } from 'src/context/ToastContext';
import {
  FlowArrowIcon,
  CircleNotchIcon,
  CheckCircleIcon,
  StackIcon,
  GitBranchIcon,
  EyeIcon,
} from '@phosphor-icons/react';
import {
  useListWorkflowTemplates,
  useInstantiateWorkflowTemplate,
} from 'src/api/rpc/hooks/workflows/useWorkflowTemplates';
import { WorkflowGraphPreview } from 'src/components/workflows/WorkflowGraphPreview';
import type { WorkflowTemplateResponse } from '@erp/rpc';
import { useNavigate } from '@tanstack/react-router';
import paths from 'src/paths';

const CATEGORY_ICONS = {
  email: FlowArrowIcon,
  ap: StackIcon,
  po: GitBranchIcon,
  integration: CheckCircleIcon,
  custom: FlowArrowIcon,
};

export function WorkflowTemplatesPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { data: templatesData, isLoading } = useListWorkflowTemplates();
  const instantiateTemplate = useInstantiateWorkflowTemplate();
  const [instantiatingTemplates, setInstantiatingTemplates] = useState<
    Set<string>
  >(new Set());
  const [previewTemplate, setPreviewTemplate] =
    useState<WorkflowTemplateResponse | null>(null);

  const templates = templatesData?.templates || [];

  const handleInstantiate = async (
    template: WorkflowTemplateResponse,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();

    setInstantiatingTemplates((prev) => new Set(prev).add(template.id));

    try {
      const workflowDefinition = await instantiateTemplate.mutateAsync({
        aiCreationId: template.id,
      });

      showToast({
        title: 'Workflow Created',
        message: `${template.name} has been instantiated successfully!`,
        color: 'success',
        icon: 'check',
      });

      // Navigate to the workflow definition detail page
      navigate({
        to: paths.authenticated.workflows.definition,
        params: { workflowDefinitionId: workflowDefinition.id },
      });
    } catch (error) {
      showToast({
        title: 'Failed to Create Workflow',
        message:
          error instanceof Error ? error.message : 'An unknown error occurred',
        color: 'error',
        icon: 'x',
      });
    } finally {
      setInstantiatingTemplates((prev) => {
        const newSet = new Set(prev);
        newSet.delete(template.id);
        return newSet;
      });
    }
  };

  const isInstantiating = (templateId: string) => {
    return instantiatingTemplates.has(templateId);
  };

  const getCategoryIcon = (category: string) => {
    return (
      CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS] || FlowArrowIcon
    );
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-7xl">
      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <CircleNotchIcon className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      )}

      {/* Templates Grid */}
      {!isLoading && templates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => {
            const Icon = getCategoryIcon(template.category || 'custom');
            const isInstantiatingTemplate = isInstantiating(template.id);

            return (
              <Card
                key={template.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => {
                  /* Could navigate to a preview page */
                }}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    {template.category && (
                      <Badge variant="secondary" className="capitalize">
                        {template.category}
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-xl">{template.name}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {template.description || 'No description available'}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  {/* Template Stats */}
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-1">
                      <StackIcon className="h-4 w-4" />
                      <span>{template.steps.length} steps</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <GitBranchIcon className="h-4 w-4" />
                      <span>{template.edges.length} connections</span>
                    </div>
                  </div>

                  {/* Tags */}
                  {template.tags && template.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {template.tags.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {template.tags.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{template.tags.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewTemplate(template);
                      }}
                      className="flex-1"
                    >
                      <EyeIcon className="mr-2 h-4 w-4" />
                      Preview
                    </Button>
                    <Button
                      size="sm"
                      onClick={(e) => handleInstantiate(template, e)}
                      disabled={isInstantiatingTemplate}
                      className="flex-1"
                    >
                      {isInstantiatingTemplate ? (
                        <>
                          <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="mr-2 h-4 w-4" />
                          Create
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && templates.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-flex p-4 bg-gray-100 rounded-full mb-4">
            <FlowArrowIcon className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Templates Available
          </h3>
          <p className="text-gray-600">
            Workflow templates will appear here when they are added to the
            system.
          </p>
        </div>
      )}

      {/* Preview Sheet */}
      <Sheet
        open={previewTemplate !== null}
        onOpenChange={(open) => !open && setPreviewTemplate(null)}
      >
        <SheetContent side="bottom" className="h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{previewTemplate?.name}</SheetTitle>
            <SheetDescription>
              {previewTemplate?.description || 'Workflow template preview'}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 px-3">
            {previewTemplate && (
              <>
                {/* Template Info */}
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4 pb-4 border-b">
                  <div className="flex items-center gap-1">
                    <StackIcon className="h-4 w-4" />
                    <span>{previewTemplate.steps.length} steps</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <GitBranchIcon className="h-4 w-4" />
                    <span>{previewTemplate.edges.length} connections</span>
                  </div>
                  {previewTemplate.tags && previewTemplate.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {previewTemplate.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Workflow Graph */}
                <WorkflowGraphPreview
                  template={previewTemplate}
                  height="500px"
                />

                {/* Action Buttons */}
                <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setPreviewTemplate(null)}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={(e) => {
                      handleInstantiate(previewTemplate, e);
                      setPreviewTemplate(null);
                    }}
                    disabled={instantiatingTemplates.has(previewTemplate.id)}
                  >
                    {instantiatingTemplates.has(previewTemplate.id) ? (
                      <>
                        <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="mr-2 h-4 w-4" />
                        Create Workflow
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
